import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import CrimeMap from "@/components/vigilante/CrimeMap";
import CrimeList from "@/components/vigilante/CrimeList";
import CrimeStats from "@/components/vigilante/CrimeStats";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Calendar, Filter, LayoutGrid, Map, ExternalLink, Settings, User, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

type CrimeReport = Tables<"crime_reports">;

const Dashboard = () => {
  const { userRole, updateUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("list");
  const [showLast24Hours, setShowLast24Hours] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  const isFromReport = location.state?.fromReport || false;

  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (isFromReport) {
      setShowLast24Hours(true);
      setActiveTab("map");
    }
  }, [isFromReport]);

  const { data: crimeReports, isLoading, error } = useQuery({
    queryKey: ["crimeReports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crime_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CrimeReport[];
    },
  });

  const filteredReports = React.useMemo(() => {
    if (!crimeReports) return [];
    
    if (showLast24Hours) {
      return crimeReports.filter(
        report => new Date(report.created_at) > new Date(twentyFourHoursAgo)
      );
    }
    
    return crimeReports;
  }, [crimeReports, showLast24Hours, twentyFourHoursAgo]);

  useEffect(() => {
    const channel = supabase
      .channel('public:crime_reports')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'crime_reports' 
      }, payload => {
        console.log('New crime report:', payload);
        toast({
          title: "New Crime Report",
          description: "A new crime has been reported in your area"
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast]);

  const handleRoleChange = async (role: "citizen" | "vigilante") => {
    if (role !== userRole) {
      await updateUserRole(role);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 container py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-okkio-orange"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 container py-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Data</CardTitle>
            </CardHeader>
            <CardContent>
              <p>There was an error loading the crime reports. Please try again later.</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">
            {userRole === "vigilante" ? "Vigilante" : "Citizen"} Dashboard
          </h1>
          <div className="flex gap-2">
            <Link to="/report-crime">
              <Button variant="default" className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Report Crime
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings className="h-4 w-4" />
              Settings
            </Button>
            {userRole === "vigilante" && (
              <Link to="/crime-map">
                <Button variant="outline" className="flex items-center gap-2">
                  <Map className="h-4 w-4" />
                  Full Screen Map
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </Link>
            )}
          </div>
        </div>
        
        {showSettings && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Account Settings</CardTitle>
              <CardDescription>Customize your account settings and preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium mb-2">User Role</h3>
                  <RadioGroup 
                    value={userRole || "citizen"} 
                    onValueChange={(value) => handleRoleChange(value as "citizen" | "vigilante")}
                    className="flex flex-col space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="citizen" id="citizen" />
                      <Label htmlFor="citizen" className="flex items-center cursor-pointer">
                        <User className="h-4 w-4 mr-2" />
                        Citizen
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="vigilante" id="vigilante" />
                      <Label htmlFor="vigilante" className="flex items-center cursor-pointer">
                        <Shield className="h-4 w-4 mr-2" />
                        Vigilante
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <CrimeStats crimeReports={filteredReports} />
        </div>
        
        <div className="flex justify-between items-center mb-4">
          <Tabs defaultValue="list" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-between items-center">
              <TabsList>
                <TabsTrigger value="list">
                  <LayoutGrid className="h-4 w-4 mr-2" />
                  List View
                </TabsTrigger>
                <TabsTrigger value="map">
                  <Map className="h-4 w-4 mr-2" />
                  Map View
                </TabsTrigger>
              </TabsList>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="last24hours" 
                    checked={showLast24Hours} 
                    onCheckedChange={setShowLast24Hours} 
                  />
                  <Label htmlFor="last24hours" className="flex items-center cursor-pointer">
                    <Calendar className="h-4 w-4 mr-2" />
                    Last 24 Hours
                  </Label>
                </div>
              </div>
            </div>
            
            <TabsContent value="list">
              <CrimeList crimeReports={filteredReports} />
            </TabsContent>
            
            <TabsContent value="map">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden h-[600px]">
                <CrimeMap 
                  crimeReports={filteredReports} 
                  showLast24Hours={showLast24Hours} 
                  initialLocation={userLocation}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
